import { useState, useEffect, useCallback, useMemo } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { fetchAttributeGroups } from "../declr_attribute_group/declrAttributeGroupInteractionService";
import { fetchUnitsOfMeasure, fetchEnumOptions } from "../../site-admin/declr_attribute_definition/declrAttributeDefinitionInteractionService";
import { fetchLocalDeclarations, setLocalDeclaration } from "./declrLocalDeclarationInteractionService";

/**
 * 🎯 LOCAL DECLARATION ENTRY CONTROLLER
 * Renders a company's Attribute Groups (Z_11) as data-entry sections, one
 * type-driven input per composed attribute, for a chosen reporting year.
 * Values are captured in the attribute's own local unit — group-level
 * roll-up/conversion happens later via Z_13_Declr_AttributeMapping.
 */
export default function DeclrLocalDeclarationController(companyId, siteKey) {
  const currentYear = new Date().getFullYear();
  const [reportingYear, setReportingYear] = useState(currentYear);
  const [groupRows, setGroupRows] = useState([]);
  const [values, setValues] = useState({}); // attributeDefinitionId -> { EnteredValue, EnteredUnitCode }
  const [units, setUnits] = useState([]);
  const [enumOptionsByAttr, setEnumOptionsByAttr] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const [groups, declared, unitRows] = await Promise.all([
        fetchAttributeGroups(companyId, { siteKey }),
        fetchLocalDeclarations(companyId, reportingYear, { siteKey }),
        fetchUnitsOfMeasure(null, { siteKey }),
      ]);
      setGroupRows(groups || []);
      setUnits(unitRows || []);

      const valueMap = {};
      for (const d of declared || []) {
        const attrId = GoldenSourceHelper.get(d, "AttributeDefinitionId");
        valueMap[attrId] = {
          EnteredValue: GoldenSourceHelper.get(d, "EnteredValue"),
          EnteredUnitCode: GoldenSourceHelper.get(d, "EnteredUnitCode"),
        };
      }
      setValues(valueMap);

      // Pre-fetch enum options for any enum-typed attribute in these groups
      const enumAttrIds = [...groupRows_uniqueEnumIds(groups)];
      const optionsEntries = await Promise.all(
        enumAttrIds.map(async (attrId) => [attrId, await fetchEnumOptions(attrId, { siteKey })])
      );
      setEnumOptionsByAttr(Object.fromEntries(optionsEntries));
    } finally {
      setLoading(false);
    }
  }, [companyId, siteKey, reportingYear]);

  function groupRows_uniqueEnumIds(groups) {
    const ids = new Set();
    for (const row of groups || []) {
      if (GoldenSourceHelper.get(row, "ValueDataType") === "enum") {
        ids.add(GoldenSourceHelper.get(row, "AttributeDefinitionId"));
      }
    }
    return ids;
  }

  useEffect(() => {
    load();
  }, [load]);

  // Group the flat (one-row-per-member) response into { group, members[] }
  const groups = useMemo(() => {
    const byId = new Map();
    for (const row of groupRows) {
      const groupId = GoldenSourceHelper.get(row, "AttributeGroupId");
      if (!byId.has(groupId)) {
        byId.set(groupId, {
          AttributeGroupId: groupId,
          GroupLabel: GoldenSourceHelper.get(row, "GroupLabel"),
          members: [],
        });
      }
      const attrId = GoldenSourceHelper.get(row, "AttributeDefinitionId");
      if (attrId) {
        byId.get(groupId).members.push({
          AttributeDefinitionId: attrId,
          AttributeLabel: GoldenSourceHelper.get(row, "AttributeLabel"),
          ValueDataType: GoldenSourceHelper.get(row, "ValueDataType"),
          DefaultUnitCode: GoldenSourceHelper.get(row, "DefaultUnitCode"),
          AllowedUnitFamily: GoldenSourceHelper.get(row, "AllowedUnitFamily"),
        });
      }
    }
    return [...byId.values()];
  }, [groupRows]);

  const unitsForFamily = (family) => units.filter((u) => GoldenSourceHelper.get(u, "UnitFamily") === family);

  const handleFieldChange = (attributeDefinitionId, field, value) => {
    setValues((prev) => ({
      ...prev,
      [attributeDefinitionId]: { ...(prev[attributeDefinitionId] || {}), [field]: value },
    }));
  };

  const handleSaveField = async (attributeDefinitionId, defaultUnitCode) => {
    setSavingId(attributeDefinitionId);
    setError("");
    try {
      const entry = values[attributeDefinitionId] || {};
      await setLocalDeclaration(
        companyId,
        attributeDefinitionId,
        reportingYear,
        entry.EnteredValue,
        entry.EnteredUnitCode || defaultUnitCode,
        { siteKey }
      );
    } catch (err) {
      setError(err.message || "Could not save value.");
    } finally {
      setSavingId(null);
    }
  };

  return {
    reportingYear,
    setReportingYear,
    groups,
    values,
    units,
    unitsForFamily,
    enumOptionsByAttr,
    loading,
    savingId,
    error,
    handleFieldChange,
    handleSaveField,
  };
}
